import { Children, type ReactNode } from 'react';

import { Intent, IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import { DownloadData } from '../../core/slots/toolbar/DownloadData.js';
import type { ChartToolbarConfig } from '../../core/types/toolbar.js';
import { Grouping as DonutChartGrouping } from '../slots/DonutChartGrouping.js';
import { DonutChartInner } from '../slots/DonutChartInner.js';
import { Labels as DonutChartLabels } from '../slots/DonutChartLabels.js';
import { Legend as DonutChartLegend } from '../slots/DonutChartLegend.js';
import { Grouping as PieChartGrouping } from '../slots/PieChartGrouping.js';
import { Labels as PieChartLabels } from '../slots/PieChartLabels.js';
import { Legend as PieChartLegend } from '../slots/PieChartLegend.js';
import type { PieChartSlots } from '../types/pie-chart.config.js';

/**
 * Generate chart config based on slots
 * @param children - CharInteraction children
 * @returns ChartInteraction slot config
 */
const iterateChartToolbarSlots = (children: ReactNode): ChartToolbarConfig => {
  let slots = {} as ChartToolbarConfig;

  Children.forEach(children, (childInteractions) => {
    if (isSlot(childInteractions, DownloadData)) {
      slots = { ...slots, downloadData: { enabled: true } };
    }
  });

  return slots;
};

/**
 * Iterate over the slotted children to merge the configuration.
 * @param children - Slotted children to iterate
 * @returns Merged config as PieChartSlots
 */
export function iterateSlots(children: ReactNode): PieChartSlots {
  let slots: PieChartSlots = {
    labels: {},
    grouping: {},
    legend: {},
    toolbar: undefined,
    innerElement: undefined,
  };

  let intents: IntentProps[] | undefined;

  Children.map(children, (child) => {
    if (isSlot(child, PieChartLabels) || isSlot(child, DonutChartLabels)) {
      slots = { ...slots, labels: child.props };
    } else if (
      isSlot(child, PieChartGrouping) ||
      isSlot(child, DonutChartGrouping)
    ) {
      slots = { ...slots, grouping: child.props };
    } else if (
      isSlot(child, PieChartLegend) ||
      isSlot(child, DonutChartLegend)
    ) {
      slots = { ...slots, legend: child.props };
    } else if (isSlot(child, ChartToolbar)) {
      const { children, ...toolbarProps } = child.props;

      const toolbarSlots = iterateChartToolbarSlots(children);

      const toolbar = { ...toolbarProps, ...toolbarSlots };
      slots = { ...slots, toolbar };
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, DonutChartInner)) {
      const inner = child.props.children;
      slots = { ...slots, innerElement: inner };
    }
  });

  if (slots.toolbar) {
    slots = {
      ...slots,
      toolbar: {
        ...slots.toolbar,
        intents,
      },
    };
  }

  return slots;
}
